import { useMemo, useState } from 'react';
import { Lock, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  Pagination,
  Stat,
  StatGroup,
  TimeRangePicker,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { AccountBreadcrumbs } from './components/account-breadcrumbs';
import {
  Button,
  Card,
  CardBody,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMe } from '@/api/me';
import {
  useEmployeeIncomes,
  useEmployeeIncomeStats,
  INCOME_ALLOWED_DEPARTMENTS,
  type Income,
} from '@/api/incomes';
import { formatMoney, formatMonthYear } from '@/lib/format';
import { paginationView } from '@/pages/customers/pagination-summary';

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * My Incomes — the signed-in employee's own commission / KPI income report.
 * Department-gated: only Board, Sales and Account may see it.
 * Features: date-range filter, sort by invoice_item_amount desc (default),
 * page-size selector, and real @ui Pagination.
 */
export default function MyIncomesPage() {
  const { data: me } = useMe();
  const department = me?.employee?.department_name ?? '';
  const allowed = INCOME_ALLOWED_DEPARTMENTS.some(
    (d) => d.toLowerCase() === department.toLowerCase(),
  );

  // Filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(20);

  const filtersJson = useMemo(() => {
    if (!fromDate && !toDate) return undefined;
    return JSON.stringify({ ...(fromDate ? { gte: fromDate } : {}), ...(toDate ? { lte: toDate } : {}) });
  }, [fromDate, toDate]);

  const params = useMemo(
    () => ({
      page,
      page_size: pageSize,
      sort_by: 'invoice_item_amount',
      sort_order: 'desc' as const,
      ...(filtersJson ? { filters: filtersJson } : {}),
    }),
    [page, pageSize, filtersJson],
  );

  const statsParams = useMemo(
    () => (filtersJson ? { filters: filtersJson } : {}),
    [filtersJson],
  );

  // Hooks must run unconditionally (React rules of hooks).
  const incomes = useEmployeeIncomes(params);
  const stats = useEmployeeIncomeStats(statsParams);

  const columns = useMemo<ColumnDef<Income, unknown>[]>(
    () => [
      { accessorKey: 'customer_name', header: 'Customer' },
      { accessorKey: 'invoice_id', header: 'Invoice' },
      { accessorKey: 'product_group_name', header: 'Product group' },
      { accessorKey: 'service_name', header: 'Service' },
      { accessorKey: 'service_type', header: 'Type' },
      {
        accessorKey: 'month_created_invoice',
        header: 'Month',
        cell: ({ getValue }) => formatMonthYear(String(getValue() ?? '') || null),
      },
      {
        accessorKey: 'invoice_item_amount',
        header: 'Amount',
        meta: { align: 'right' },
        cell: ({ getValue }) => formatMoney(getValue() as number),
      },
      {
        accessorKey: 'payment_date',
        header: 'Payment',
        cell: ({ getValue }) => formatMonthYear(String(getValue() ?? '') || null),
      },
      { accessorKey: 'kpi_type', header: 'KPI type' },
      {
        accessorKey: 'kpi_rate',
        header: 'KPI rate',
        cell: ({ getValue }) => `${((getValue() as number) * 100).toFixed(0)}%`,
      },
      {
        accessorKey: 'kpi_amount',
        header: 'KPI amount',
        meta: { align: 'right' },
        cell: ({ getValue }) => formatMoney(getValue() as number),
      },
    ],
    [],
  );

  if (!allowed) {
    return (
      <Container size="full">
        <PageHeader breadcrumbs={<AccountBreadcrumbs current="My Incomes" />} title="My Incomes" />
        <EmptyState
          icon={<Lock size={24} />}
          title="Not available"
          description="Income report is limited to Board, Sales and Account departments."
        />
      </Container>
    );
  }

  const rows = incomes.data?.results ?? [];
  const pagination = incomes.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.total_pages ?? 1;
  const { pageCount, summary: footerSummary } = paginationView(page, pageSize, rows.length, total, totalPages);

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const hasFilters = fromDate || toDate;

  return (
    <Container size="full">
      <PageHeader
        breadcrumbs={<AccountBreadcrumbs current="My Incomes" />}
        title="My Incomes"
        description="Your commission and KPI income"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card flat>
          <CardBody>
            {stats.isLoading ? (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                <Skeleton style={{ height: 24 }} />
                <Skeleton style={{ height: 24 }} />
              </div>
            ) : (
              <StatGroup columns={2}>
                <Stat
                  label="Total amount"
                  dotColor="var(--vn-success-solid)"
                  value={formatMoney(stats.data?.total_amount)}
                />
                <Stat
                  label="Invoice items"
                  dotColor="var(--vn-info-solid)"
                  value={stats.data?.total_invoice_items ?? '—'}
                />
              </StatGroup>
            )}
          </CardBody>
        </Card>

        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              <TimeRangePicker
                showLive={false}
                presets={[]}
                from={fromDate}
                to={toDate}
                triggerLabel={fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select dates'}
                onApply={(v) => {
                  setFromDate((v.from ?? '').slice(0, 10));
                  setToDate((v.to ?? '').slice(0, 10));
                  setPage(1);
                }}
              />
              {hasFilters && (
                <Button size="sm" variant="secondary" onClick={handleReset}>
                  Reset
                </Button>
              )}
            </div>
          }
          end={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ minWidth: 180 }}>
                <DropdownMenuLabel>Rows per page</DropdownMenuLabel>
                <div style={{ padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => { setPageSize(Number(v) as PageSizeOption); setPage(1); }}
                  >
                    <SelectTrigger style={{ width: '100%' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        {incomes.isError ? (
          <Alert
            tone="danger"
            title="Failed to load income report"
            actions={
              <Button size="sm" variant="ghost" onClick={() => incomes.refetch()}>
                Retry
              </Button>
            }
          />
        ) : incomes.isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => `${r.invoice_id}-${r.service_id}`}
            emptyState={<EmptyState title="No income records" />}
            footer={
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={(p) => setPage(p)}
                summary={footerSummary}
              />
            }
          />
        )}
      </div>
    </Container>
  );
}
